/**
 * Shared parsing for GET /api/v1/stock-alert-thresholds (list) responses.
 * Keeps Alerts inline editors aligned with StockAlertThresholdsPanel.
 */

export function parseJsonIfNeeded (v) {
  if (v == null) return null
  if (typeof v === 'string') {
    try {
      return JSON.parse(v)
    } catch (e) {
      return null
    }
  }
  return v
}

export function extractThresholdGetItems (rawBody) {
  if (Array.isArray(rawBody)) return rawBody
  var body = parseJsonIfNeeded(rawBody)
  if (body == null) body = rawBody
  if (body == null) return []
  if (typeof body === 'string') {
    body = parseJsonIfNeeded(body)
    if (body == null || typeof body !== 'object') return []
  }
  if (Array.isArray(body)) return body
  if (Array.isArray(body.items)) return body.items
  if (body.items && typeof body.items === 'object' && !Array.isArray(body.items)) {
    return Object.keys(body.items).map(function (k) { return body.items[k] }).filter(Boolean)
  }
  var data = body.data
  data = parseJsonIfNeeded(data)
  if (data != null && typeof data === 'object') {
    if (Array.isArray(data.items)) return data.items
    if (Array.isArray(data)) return data
  }
  if (body.result && typeof body.result === 'object') {
    if (Array.isArray(body.result.items)) return body.result.items
    if (Array.isArray(body.result)) return body.result
  }
  if (body.payload && typeof body.payload === 'object' && Array.isArray(body.payload.items)) return body.payload.items
  if (Array.isArray(body.listings)) return body.listings
  if (Array.isArray(body.rows)) return body.rows
  if (Array.isArray(body.results)) return body.results
  if (Array.isArray(body.content)) return body.content
  if (Array.isArray(body.records)) return body.records
  if (body._embedded && typeof body._embedded === 'object') {
    var emb = body._embedded
    if (Array.isArray(emb.items)) return emb.items
    if (Array.isArray(emb.stockAlertThresholds)) return emb.stockAlertThresholds
    var ek = Object.keys(emb)
    for (var ei = 0; ei < ek.length; ei++) {
      if (Array.isArray(emb[ek[ei]])) return emb[ek[ei]]
    }
  }
  var dataScan = parseJsonIfNeeded(body.data)
  if (dataScan == null) dataScan = body.data
  if (dataScan != null && typeof dataScan === 'object' && !Array.isArray(dataScan)) {
    var dk = Object.keys(dataScan)
    for (var di = 0; di < dk.length; di++) {
      var dv = dataScan[dk[di]]
      if (Array.isArray(dv) && dv.length && typeof dv[0] === 'object') return dv
    }
  }
  var bk = Object.keys(body)
  for (var bi = 0; bi < bk.length; bi++) {
    var bv = body[bk[bi]]
    if (!Array.isArray(bv) || !bv.length || typeof bv[0] !== 'object') continue
    var z = bv[0]
    if (z.product_id != null || z.productId != null || z.listing_id != null || z.asin != null) return bv
  }
  return []
}

export function productIdFromThresholdRow (row) {
  if (!row || typeof row !== 'object') return ''
  if (row.product_id != null) return String(row.product_id)
  if (row.productId != null) return String(row.productId)
  if (row.listing_id != null) return String(row.listing_id)
  if (row.id != null) return String(row.id)
  return ''
}

/** vue-resource uses `body`; some stacks expose parsed JSON on `data`. */
export function httpResponseEntity (res) {
  if (res == null) return null
  if (res.body !== undefined && res.body !== null && res.body !== '') return res.body
  if (res.data !== undefined && res.data !== null && res.data !== '') return res.data
  if (res.bodyText != null && res.bodyText !== '') return res.bodyText
  return null
}

/** Stock row / listing object: same id used in GET `product_ids` and threshold row keys (snake_case from API is common). */
export function listingIdFromProduct (p) {
  if (!p || typeof p !== 'object') return ''
  if (p.productId !== undefined && p.productId !== null && p.productId !== '') return String(p.productId)
  if (p.product_id !== undefined && p.product_id !== null && p.product_id !== '') return String(p.product_id)
  if (p.id !== undefined && p.id !== null && p.id !== '') return String(p.id)
  return ''
}

export function unwrapApiNumber (v) {
  if (v === undefined || v === null || v === '') return null
  if (typeof v === 'number' && !isNaN(v)) return v
  if (typeof v === 'string') {
    var t = v.trim()
    if (t === '') return null
    var n = parseFloat(t)
    return isNaN(n) ? null : n
  }
  if (typeof v === 'object' && v !== null) {
    if ('value' in v) return unwrapApiNumber(v.value)
    if ('$numberDecimal' in v) return unwrapApiNumber(v.$numberDecimal)
  }
  var x = Number(v)
  return isNaN(x) ? null : x
}

export function fbmThresholdFromSettings (s) {
  if (!s || typeof s !== 'object') return null
  var t = s.threshold
  if (t === undefined || t === null) t = s.fbm_threshold
  if (t === undefined || t === null) t = s.redThreshold
  if (t === undefined || t === null) t = s.red_threshold
  var n = unwrapApiNumber(t)
  return n !== null && n >= 0 ? n : null
}

export function formFieldsFromFbaSettings (s) {
  s = s || {}
  function numNonNeg (v) {
    var n = unwrapApiNumber(v)
    if (n === null) return null
    return n >= 0 ? n : null
  }
  function numPos (v) {
    var n = unwrapApiNumber(v)
    if (n === null) return null
    return n > 0 ? n : null
  }
  function pick (a, b) {
    return (a !== undefined && a !== null) ? a : b
  }
  return {
    amberThreshold: numNonNeg(pick(s.amberThreshold, s.amber_threshold)),
    redThreshold: numNonNeg(pick(s.redThreshold, s.red_threshold)),
    amberDays: numNonNeg(pick(s.amberDays, s.amber_days)),
    redDays: numNonNeg(pick(s.redDays, s.red_days)),
    velocityLookbackDays: numPos(pick(s.velocityLookbackDays, s.velocity_lookback_days))
  }
}



// WEBPACK FOOTER //
// ./src/utils/stockAlertThresholdsApiParse.js